import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

// CapacitorDeepLinkHandler — wires up Universal-Link arrivals so that
// tapping a magic-link / OAuth callback URL in an email opens INSIDE
// the native iOS app instead of bouncing to Safari.
//
// How it works:
//   1. iOS reads `apple-app-site-association` at https://globaleyes.tv
//      and learns the app claims /magic-login/* etc.
//   2. When a user taps such a URL, iOS launches our app and the
//      Capacitor "App" plugin fires `appUrlOpen` with the full URL.
//   3. We strip the origin and React-Router-navigate to the path —
//      MagicLoginPage / GoogleAuthCallback already handle the rest.
//
// Edge cases handled:
//   - Cold-start: when iOS launches the app from a tap on a deep link,
//     `appUrlOpen` listeners registered later may MISS the original
//     event. We explicitly call `App.getLaunchUrl()` once on mount to
//     drain any pending launch URL.
//   - Foreign hosts: we only navigate when the URL's host matches our
//     known origins (globaleyes.tv, www.globaleyes.tv, or whatever
//     REACT_APP_BACKEND_URL points at). Prevents a malicious URL like
//     `https://attacker.com/admin` from hijacking the SPA router.
//   - Duplicate fires: iOS sometimes emits the same URL twice during
//     a single launch (once via getLaunchUrl, once via appUrlOpen).
//     We dedupe on the exact URL string.
//
// On web (where window.Capacitor.isNativePlatform === false) this hook
// is a no-op, so it's safe to include in App.js unconditionally.
const ALLOWED_HOSTS = new Set([
  "globaleyes.tv",
  "www.globaleyes.tv",
  // Allow the preview/dev origin too so QA on a dev pod still routes.
  (() => {
    try {
      return new URL(process.env.REACT_APP_BACKEND_URL || "").host;
    } catch {
      return "";
    }
  })(),
].filter(Boolean));

const CapacitorDeepLinkHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!window.Capacitor?.isNativePlatform?.()) return undefined;

    let cleanup = null;
    let cancelled = false;
    let lastHandledUrl = "";

    const dispatch = (url) => {
      if (!url || url === lastHandledUrl) return;
      lastHandledUrl = url;
      try {
        const u = new URL(url);
        // Host whitelist — refuse to navigate on URLs not from us.
        if (ALLOWED_HOSTS.size > 0 && !ALLOWED_HOSTS.has(u.host)) {
          console.warn("Ignoring deep-link from foreign host:", u.host);
          return;
        }
        const target = `${u.pathname}${u.search}${u.hash}`;
        // Empty path → no-op (just an app open, nothing to route)
        if (!target || target === "/") return;
        navigate(target, { replace: false });
      } catch (e) {
        console.warn("Could not parse incoming deep-link URL:", url, e);
      }
    };

    (async () => {
      try {
        const mod = await import("@capacitor/app");
        const App = mod.App || mod.default?.App;
        if (!App || cancelled) return;

        // 1. Drain any cold-start launch URL FIRST. If iOS launched the
        //    app from a tap on a magic-link, this is where it surfaces.
        try {
          const launch = await App.getLaunchUrl?.();
          if (launch?.url) dispatch(launch.url);
        } catch (e) {
          // getLaunchUrl unavailable on older Capacitor — non-fatal.
        }

        // 2. Subscribe for runtime URL opens (foreground re-entries).
        const handle = await App.addListener("appUrlOpen", (event) => {
          dispatch(event.url);
        });
        cleanup = () => handle.remove();
      } catch (e) {
        console.warn("@capacitor/app deep-link wiring failed:", e);
      }
    })();

    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, [navigate]);

  return null;
};

export default CapacitorDeepLinkHandler;
